// src/components/digital_twin/SensorInfoDrawer.jsx
// --- UPDATE (Digital Twin MOW+ v1) ---------------------------------------------------------
//   FIXES
//   • Battery "98% (Optimal)" and signal "-65 dBm (Strong)" were hard-coded for every sensor —
//     now the sensor's own battery_level / signal_strength (rssi) or "—".
//   • A reading of 0 PSI showed as "0"→ falsy → the drawer fell back to 0 for every missing
//     value too; values now come from readingValue() and "—" when there is none.
//   • Coordinates crashed when latitude/longitude arrived as strings (toFixed on a string).
//   • ONLINE meant "a reading exists" even if it was hours old — stale readings (15+ min) now
//     show STALE; historical replay shows REPLAY.
//   FEATURES (all optional props — the drawer still works with { sensor, reading, isOpen, onClose })
//   • Trend chart of the live stream (WebSocket) / history, with min-max thresholds and the ML
//     "normal band" (mean ± 2σ).
//   • FlowSense ML: learned normal, z-score, rate of change, anomaly (spike / drop / stuck),
//     PSI condition rule, plain-language explanation; Ask FlowSense about this sensor.
//   • Linked asset / pipeline, Fly to, open the linked asset, copy device id.
// --- END UPDATE ---------------------------------------------------------------------------
import React from 'react';
import {
    Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent,
    DrawerCloseButton, Box, VStack, HStack, Text, Divider, Tag, Icon,
    Heading, Badge, useColorModeValue, Flex
} from '@chakra-ui/react';
// --- UPDATE
import { Button, SimpleGrid, Tooltip, useClipboard, Spinner, IconButton } from '@chakra-ui/react';
import { FaBrain, FaCrosshairs, FaCopy, FaCheck, FaLink } from 'react-icons/fa';
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import TwinSparkline from './TwinSparkline';
import { readingValue, readingWord, readingTime, unitOf, num, d, matchPsiCondition, liveSeverity, explainSensor, sensorKey, coords, TWIN_AI_DEFAULTS } from './twinIntel';
// --- END UPDATE
import { keyframes } from '@emotion/react';
import { MdLocationOn, MdAccessTime, MdWifi, MdBatteryFull, MdSensors } from 'react-icons/md';
import { FaWater, FaTachometerAlt } from 'react-icons/fa';

const pulseRing = keyframes`
  0% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(72, 187, 120, 0); }
  100% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
`;

const InfoRow = ({ icon, label, value }) => {
    const textColor = useColorModeValue('gray.800', 'white');
    const rowBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const rowBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
    const iconBg = useColorModeValue('white', 'gray.700');

    return (
        <HStack w="100%" align="center" spacing={4} p={3} bg={rowBg} borderRadius="lg" borderWidth="1px" borderColor={rowBorder}>
            <Flex p={2} bg={iconBg} borderRadius="md" shadow="sm">
                <Icon as={icon} color="blue.500" boxSize={4} />
            </Flex>
            <Box>
                <Text fontSize="xs" color="gray.500" fontWeight="700" textTransform="uppercase">{label}</Text>
                <Text fontWeight="800" color={textColor}>{value || 'N/A'}</Text>
            </Box>
        </HStack>
    );
};

// --- UPDATE: helpers
const SEV_COLOR = { critical: 'red', warning: 'orange', normal: 'green', unknown: 'gray' };
const ago = (ms) => { if (ms === null) return null; const s = Math.max(0, Math.round((Date.now() - ms) / 1000)); return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.floor(s / 60)} min ago` : `${Math.floor(s / 3600)} h ago`; };
const batteryText = (sensor) => { const b = num(sensor?.battery_level ?? sensor?.battery ?? sensor?.battery_pct); return b === null ? '—' : `${Math.round(b)}%${b < 20 ? ' (replace soon)' : ''}`; };
const signalText = (sensor) => { const r = num(sensor?.signal_strength ?? sensor?.rssi ?? sensor?.signal_dbm); return r === null ? '—' : `${Math.round(r)} dBm${r >= -70 ? ' (strong)' : r >= -90 ? ' (fair)' : ' (weak)'}`; };
// --- END UPDATE

const SensorInfoDrawer = ({
    sensor, reading, isOpen, onClose,
    // --- UPDATE: optional live / ML props
    history = null, intelRow = null, psiConditions = [], isLive = true, historicalTimestamp = null,
    linkedEntity = null, onFlyTo = null, onOpenEntity = null, onRequestHistory = null,
    // --- END UPDATE
}) => {
    const drawerBg = useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(26, 32, 44, 0.95)');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const cardBg = useColorModeValue('white', 'gray.800');
    // --- UPDATE: hoisted theme values for the new sections
    const aiBg = useColorModeValue('purple.50', 'rgba(128,90,213,0.12)');
    const tileBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const [answer, setAnswer] = React.useState(null);
    const [asking, setAsking] = React.useState(false);
    const { hasCopied, onCopy } = useClipboard(sensor ? sensorKey(sensor) : '');
    const requestedRef = React.useRef(null);
    React.useEffect(() => { setAnswer(null); }, [sensor]);
    React.useEffect(() => {
        if (!isOpen || !sensor || !onRequestHistory) return;
        const k = sensorKey(sensor);
        if (requestedRef.current === k) return;
        requestedRef.current = k;
        onRequestHistory(sensor);
    }, [isOpen, sensor, onRequestHistory]);
    // --- END UPDATE

    if (!sensor) return null;

    // --- UPDATE: `isLive` is now the page's live/replay mode (prop); "has a fresh reading" is isLive2 below
    const isWaterLevel = sensor.type === 'water_level';
    const MainIcon = isWaterLevel ? FaWater : FaTachometerAlt;
    const valueLabel = isWaterLevel ? 'Level' : 'PSI';
    // --- UPDATE: 0 is a valid reading; missing values are "—"
    const valueData = readingWord(reading) || (readingValue(reading) !== null ? d(readingValue(reading)) : '—');
    const ts = readingTime(reading);
    const isStale = !!(isLive && ts !== null && Date.now() - ts > TWIN_AI_DEFAULTS.staleMs);
    const isLive2 = !!reading && !isStale; // replaces the original "reading exists" test
    const timeString = ts !== null ? `${new Date(ts).toLocaleTimeString()} (${ago(ts)})` : 'N/A';
    const sev = intelRow ? { severity: intelRow.severity, reason: intelRow.reason } : liveSeverity(sensor, reading);
    const condition = !isWaterLevel ? matchPsiCondition(readingValue(reading), psiConditions) : null;
    const ml = intelRow?.ml || null;
    const band = ml && ml.samples >= TWIN_AI_DEFAULTS.minSamples && num(ml.std) !== null ? { lo: ml.mean - 2 * ml.std, hi: ml.mean + 2 * ml.std } : null;
    const c = coords(sensor);
    const unit = unitOf(sensor);
    const explanation = explainSensor(sensor, intelRow || { hasReading: !!reading, reading, severity: sev.severity, reason: sev.reason, ml: null }, { condition });
    const askAboutSensor = async () => {
        setAsking(true);
        const question = `Explain the current state of sensor ${sensorKey(sensor)} for an operator and say what to do.`;
        try {
            const r = await askFlowSenseAi(question, { module: 'digital_twin', sensor: { id: sensorKey(sensor), type: sensor.type, reading: readingValue(reading), unit, severity: sev.severity, reason: sev.reason, ml: ml ? { mean: ml.mean, std: ml.std, z: ml.z, rate: ml.rate, samples: ml.samples, kind: ml.kind } : null, condition: condition ? condition.status_label : null, stale: isStale }, rules: 'Use only these numbers.' }, 'digital_twin');
            setAnswer({ text: r?.answer || explanation, source: r?.answer ? 'ai' : 'rules', model: r?.model || null });
        } catch (_) {
            setAnswer({ text: explanation, source: 'rules' });
        } finally { setAsking(false); }
    };
    // --- END UPDATE

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
            <DrawerOverlay backdropFilter="blur(4px)" />
            <DrawerContent bg={drawerBg} backdropFilter="blur(16px)" borderLeftWidth="1px" borderColor={borderColor}>
                <DrawerCloseButton mt={2} />
                <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} bg={headerBg}>
                    <HStack spacing={3}>
                        <Flex p={2} bg="blue.500" borderRadius="lg" shadow="md">
                            <Icon as={MdSensors} color="white" boxSize={5} />
                        </Flex>
                        <Heading size="md" fontWeight="800">Sensor Intelligence</Heading>
                    </HStack>
                </DrawerHeader>

                <DrawerBody>
                    <VStack spacing={5} align="stretch" py={4}>

                        {/* Title & Status */}
                        <Box textAlign="center" mb={2}>
                            <Heading size="lg" fontWeight="900" mb={2}>
                                {sensor.name || `Sensor ${sensor.device_id || sensor.id}`}
                            </Heading>
                            <HStack justify="center" spacing={3} mt={2}>
                                <Tag colorScheme="blue" size="md" borderRadius="full" px={3} fontWeight="bold">
                                    {isWaterLevel ? 'Water Level' : 'Pressure'}
                                </Tag>
                                {/* --- UPDATE: STALE / REPLAY states */}
                                <Badge colorScheme={!isLive ? 'purple' : isLive2 ? 'green' : isStale ? 'orange' : 'gray'} variant="subtle" px={3} py={1} borderRadius="full" display="flex" alignItems="center">
                                    {isLive && isLive2 && <Box as="span" w="2" h="2" borderRadius="full" bg="green.500" mr={2} animation={`${pulseRing} 2s infinite`} />}
                                    {!isLive ? 'REPLAY' : isLive2 ? 'ONLINE' : isStale ? 'STALE' : 'OFFLINE'}
                                </Badge>
                                <Badge colorScheme={SEV_COLOR[sev.severity] || 'gray'} borderRadius="full" px={3} py={1}>{String(sev.severity).toUpperCase()}</Badge>
                                {/* --- END UPDATE */}
                            </HStack>
                            {/* --- UPDATE: device id + actions */}
                            <HStack justify="center" spacing={2} mt={3}>
                                <Text fontSize="xs" color={muted} fontFamily="mono">{sensorKey(sensor)}</Text>
                                <Tooltip label={hasCopied ? 'Copied' : 'Copy device ID'} hasArrow><IconButton size="xs" variant="ghost" icon={<Icon as={hasCopied ? FaCheck : FaCopy} />} aria-label="Copy device ID" onClick={onCopy} /></Tooltip>
                                {onFlyTo && c && <Button size="xs" leftIcon={<Icon as={FaCrosshairs} />} variant="outline" colorScheme="blue" onClick={() => onFlyTo(c, sensor)}>Fly to</Button>}
                                {onOpenEntity && linkedEntity && <Button size="xs" leftIcon={<Icon as={FaLink} />} variant="outline" colorScheme="purple" onClick={() => onOpenEntity(linkedEntity)}>Open {linkedEntity.isPipeline ? 'pipeline' : 'asset'}</Button>}
                            </HStack>
                            {/* --- END UPDATE */}
                        </Box>

                        <Divider borderColor={borderColor} />

                        {/* Live Telemetry Display */}
                        <Box p={6} borderWidth="1px" borderColor={isLive2 ? 'blue.400' : borderColor} borderRadius="2xl" bg={cardBg} shadow="lg" textAlign="center" position="relative" overflow="hidden">
                            {isLive2 && <Box position="absolute" top="-20px" right="-20px" w="100px" h="100px" bg="blue.100" opacity={0.2} borderRadius="full" filter="blur(20px)" />}
                            <Icon as={MainIcon} color={isLive2 ? "blue.500" : "gray.400"} boxSize={10} mb={3} />
                            <Text fontSize="sm" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="wide">Current {valueLabel}</Text>
                            <Heading size="3xl" color={isLive2 ? "blue.500" : "gray.500"} fontWeight="900" mt={2} aria-label={`Current ${valueLabel}`}>
                                {valueData}
                            </Heading>
                            {/* --- UPDATE: reason + PSI rule */}
                            <Text fontSize="xs" color={muted} mt={2}>{sev.reason}</Text>
                            {condition && (
                                <Badge mt={2} borderRadius="full" px={2} bg={String(condition.color_code || '').startsWith('#') ? condition.color_code : undefined} colorScheme={String(condition.color_code || '').startsWith('#') ? undefined : condition.color_code} color="white">
                                    {condition.status_label} ({condition.min_psi}–{condition.max_psi} PSI)
                                </Badge>
                            )}
                            {/* --- END UPDATE */}
                            <HStack justify="center" mt={4} color="gray.500">
                                <Icon as={MdAccessTime} />
                                <Text fontSize="xs" fontWeight="600">{!isLive && historicalTimestamp ? `Replay at ${new Date(historicalTimestamp).toLocaleString()}` : `Last Sync: ${timeString}`}</Text>
                            </HStack>
                        </Box>

                        {/* --- UPDATE: trend + ML */}
                        <Box>
                            <HStack justify="space-between" mb={2}><Heading size="sm" fontWeight="800">Trend</Heading><Text fontSize="2xs" color={muted}>{band ? 'shaded = ML normal band (mean ± 2σ)' : ''}</Text></HStack>
                            <TwinSparkline data={history || []} height={80} minThreshold={isWaterLevel ? null : sensor.min_threshold} maxThreshold={isWaterLevel ? null : sensor.max_threshold} band={band} color={sev.severity === 'critical' ? '#ef4444' : sev.severity === 'warning' ? '#f59e0b' : '#3b82f6'} ariaLabel={`${sensorKey(sensor)} trend`} emptyText={isLive ? 'Waiting for readings from the live feed…' : 'No history at this time'} />
                        </Box>
                        <Box p={4} bg={aiBg} borderRadius="xl">
                            <HStack mb={2} justify="space-between"><HStack><Icon as={FaBrain} color="purple.400" /><Heading size="sm" fontWeight="800">FlowSense ML</Heading></HStack>
                                {intelRow?.anomaly && <Badge colorScheme={intelRow.anomaly.level === 'critical' ? 'red' : 'orange'}>{intelRow.anomaly.kind === 'drop' ? 'Sudden drop' : 'Spike'}</Badge>}
                                {intelRow?.flat && <Badge colorScheme="yellow">Possibly stuck</Badge>}</HStack>
                            <SimpleGrid columns={2} spacing={2} mb={3}>
                                <Box bg={tileBg} p={2} borderRadius="md"><Text fontSize="2xs" color={muted} fontWeight="700">LEARNED NORMAL</Text><Text fontWeight="800">{ml && ml.samples >= TWIN_AI_DEFAULTS.minSamples ? `${d(ml.mean, 1)} ± ${d(ml.std, 1)}${unit ? ` ${unit}` : ''}` : '—'}</Text></Box>
                                <Box bg={tileBg} p={2} borderRadius="md"><Text fontSize="2xs" color={muted} fontWeight="700">Z-SCORE</Text><Text fontWeight="800">{ml && ml.z !== null ? d(ml.z, 1) : '—'}</Text></Box>
                                <Box bg={tileBg} p={2} borderRadius="md"><Text fontSize="2xs" color={muted} fontWeight="700">RATE / MIN</Text><Text fontWeight="800">{ml && num(ml.rate) !== null ? d(ml.rate, 2) : '—'}</Text></Box>
                                <Box bg={tileBg} p={2} borderRadius="md"><Text fontSize="2xs" color={muted} fontWeight="700">READINGS LEARNED</Text><Text fontWeight="800">{ml ? ml.samples : 0}</Text></Box>
                            </SimpleGrid>
                            <Text fontSize="sm" whiteSpace="pre-wrap">{answer ? answer.text : explanation}</Text>
                            <HStack mt={2} justify="space-between">
                                {answer ? <Badge colorScheme={answer.source === 'ai' ? 'purple' : 'gray'} fontSize="2xs">{answer.source === 'ai' ? `FlowSense AI${answer.model ? ` · ${answer.model}` : ''}` : 'FlowSense rules'}</Badge> : <span />}
                                <Button size="xs" colorScheme="purple" variant="outline" leftIcon={asking ? <Spinner size="xs" /> : <Icon as={FaBrain} />} onClick={askAboutSensor} isDisabled={asking}>Ask FlowSense</Button>
                            </HStack>
                        </Box>
                        {/* --- END UPDATE */}

                        <Divider borderColor={borderColor} />

                        {/* Hardware & Location Details */}
                        <Heading size="sm" fontWeight="800" mt={2}>Hardware Information</Heading>
                        <VStack spacing={3}>
                            {/* --- UPDATE: real values only (was hard-coded 98 % / -65 dBm) */}
                            <InfoRow icon={MdLocationOn} label="Coordinates" value={c ? `${c.lat.toFixed(4)}, ${c.lng.toFixed(4)}` : '—'} />
                            <InfoRow icon={MdBatteryFull} label="Battery Status" value={batteryText(sensor)} />
                            <InfoRow icon={MdWifi} label="Signal Strength" value={signalText(sensor)} />
                            {(num(sensor.min_threshold) !== null || num(sensor.max_threshold) !== null) && <InfoRow icon={FaTachometerAlt} label="Thresholds" value={`${d(sensor.min_threshold)} – ${d(sensor.max_threshold)}${unit ? ` ${unit}` : ''}`} />}
                            {linkedEntity && <InfoRow icon={FaLink} label={linkedEntity.isPipeline ? 'Linked pipeline' : 'Linked asset'} value={linkedEntity.name || String(linkedEntity.id)} />}
                            {/* --- END UPDATE */}
                        </VStack>

                    </VStack>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};

export default SensorInfoDrawer;
